import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '../services/api_services';
import auth_service from '../services/auth_service';
import toast from 'react-hot-toast';

const SavedPrescriptionsSelector = ({ isOpen, onClose, onSelectPrescription }) => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPrescriptions();
    }
  }, [isOpen]);

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);
      const customerId = auth_service.getAccessTokenData()?.id;
      
      if (customerId) {
        const response = await apiService.getPrescriptionByCustomerId(customerId);
        if (response.success) {
          setPrescriptions(response.data || []);
        }
      }
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
      toast.error("Failed to load saved prescriptions", {
        duration: 3000,
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPrescription = (prescription) => {
    setSelectedPrescription(prescription);
  };

  const handleUseSelectedPrescription = () => {
    if (selectedPrescription) {
      // Parse prescription data if it's a string
      let prescriptionData = selectedPrescription.prescription;
      if (typeof prescriptionData === 'string') {
        try {
          prescriptionData = JSON.parse(prescriptionData);
        } catch (error) {
          console.error("Error parsing prescription data:", error);
          toast.error("Invalid prescription data");
          return;
        }
      }

      // Convert to the expected format
      const formattedPrescription = {
        right: {
          sph: prescriptionData.right?.sph || '0.00',
          cyl: prescriptionData.right?.cyl || '0.00',
          axis: prescriptionData.right?.axis || '0',
          add: prescriptionData.right?.add || '0.00',
        },
        left: {
          sph: prescriptionData.left?.sph || '0.00',
          cyl: prescriptionData.left?.cyl || '0.00',
          axis: prescriptionData.left?.axis || '0',
          add: prescriptionData.left?.add || '0.00',
        },
        pd: prescriptionData.pd || '',
        rightPD: prescriptionData.rightPD || '',
        leftPD: prescriptionData.leftPD || '',
        hasTwoPD: prescriptionData.hasTwoPD || false,
      };

      onSelectPrescription(formattedPrescription);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 text-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Select Saved Prescription</h2>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading saved prescriptions...</p>
              </div>
            ) : prescriptions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-600 mb-2">No saved prescriptions found</p>
                <p className="text-sm text-gray-500">Create a prescription first to use this feature</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600 mb-4">Choose a prescription to use for this order:</p>
                
                {prescriptions.map((prescription, index) => {
                  const prescriptionData = typeof prescription.prescription === 'string' 
                    ? JSON.parse(prescription.prescription) 
                    : prescription.prescription;
                  
                  return (
                    <div
                      key={prescription.id}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPrescription?.id === prescription.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleSelectPrescription(prescription)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-bold text-sm">{index + 1}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800">
                              {prescription.prescription_name || `Prescription #${index + 1}`}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Created: {new Date(prescription.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedPrescription?.id === prescription.id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedPrescription?.id === prescription.id && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-700 flex items-center gap-2">
                            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 font-bold text-xs">R</span>
                            </div>
                            Right Eye
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="grid grid-cols-2 gap-2">
                              <div>SPH: <span className="font-medium">{prescriptionData.right?.sph || 'N/A'}</span></div>
                              <div>CYL: <span className="font-medium">{prescriptionData.right?.cyl || 'N/A'}</span></div>
                              <div>Axis: <span className="font-medium">{prescriptionData.right?.axis || 'N/A'}</span></div>
                              <div>ADD: <span className="font-medium">{prescriptionData.right?.add || 'N/A'}</span></div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-700 flex items-center gap-2">
                            <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-green-600 font-bold text-xs">L</span>
                            </div>
                            Left Eye
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="grid grid-cols-2 gap-2">
                              <div>SPH: <span className="font-medium">{prescriptionData.left?.sph || 'N/A'}</span></div>
                              <div>CYL: <span className="font-medium">{prescriptionData.left?.cyl || 'N/A'}</span></div>
                              <div>Axis: <span className="font-medium">{prescriptionData.left?.axis || 'N/A'}</span></div>
                              <div>ADD: <span className="font-medium">{prescriptionData.left?.add || 'N/A'}</span></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {prescriptionData.pd && (
                        <div className="mt-3 bg-amber-50 rounded-lg p-3">
                          <div className="text-sm">
                            <span className="font-medium text-amber-800">PD: {prescriptionData.pd} mm</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {prescriptions.length > 0 && (
            <div className="bg-gray-50 p-6 flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUseSelectedPrescription}
                disabled={!selectedPrescription}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  selectedPrescription
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Use Selected Prescription
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SavedPrescriptionsSelector;